// Uploaded drum loops (#430, migration 0015): loops an admin added in /admin,
// listed beside the bundled 500 in every kit picker for members with access.
//
// The audio is in a private bucket, so each loop plays from a signed URL made
// when the list is fetched (valid half a day; the player fetches a loop when
// it first needs it and keeps the decoded buffer). Signed out, or on a build
// with no backend, there are none and the pickers show the bundled set alone.

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { bootAuth, getAuthState, getSupabaseRaw, subscribeAuth } from '@/auth/session';
import { loadDrumManifest, type DrumLoop } from '@/audio/drumLoops';
import { uploadedLoopKey } from '@/audio/loopFit';

const TABLE = 'drum_loops';
export const LOOP_BUCKET = 'drum-loops';
const SIGNED_SECONDS = 12 * 60 * 60;

type Row = Record<string, unknown>;
type Result<T> = { data: T | null; error: { message: string } | null };

/** The slice of supabase-js this module uses (tests fake it). */
export interface LoopsClient {
  from(table: string): {
    select(columns: string): PromiseLike<Result<Row[]>>;
    insert(row: Row): { select(columns: string): PromiseLike<Result<Row[]>> };
    delete(): { eq(column: string, value: string): PromiseLike<Result<unknown>> };
  };
  storage: {
    from(bucket: string): {
      upload(
        path: string,
        body: Blob,
        options: { contentType: string; upsert: boolean },
      ): PromiseLike<Result<unknown>>;
      remove(paths: string[]): PromiseLike<Result<unknown>>;
      createSignedUrls(
        paths: string[],
        expiresIn: number,
      ): PromiseLike<Result<{ path: string | null; signedUrl: string }[]>>;
    };
  };
}

let testClient: LoopsClient | null = null;

async function client(): Promise<LoopsClient | null> {
  if (testClient) return testClient;
  await bootAuth();
  return getSupabaseRaw() as LoopsClient | null;
}

/** An uploaded loop as the pickers and the player read it. */
export interface UploadedLoop extends DrumLoop {
  id: string;
  storagePath: string;
  rights: string;
}

const num = (v: unknown): number | null => {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
};
const str = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() ? v.trim() : undefined;

/** A row read back is untrusted: anything the player could mis-read is dropped. */
export function loopOfRow(row: Row, url: string | undefined): UploadedLoop | null {
  const id = str(row.id);
  const label = str(row.label);
  const path = str(row.storage_path);
  const bpm = num(row.bpm);
  const beats = num(row.beats);
  const beatsPerBar = num(row.beats_per_bar);
  const seconds = num(row.seconds);
  if (!id || !label || !path || !url || !bpm || !beats || !beatsPerBar || !seconds) return null;
  if (beats % beatsPerBar !== 0) return null;
  return {
    id,
    key: uploadedLoopKey(id),
    label,
    category: str(row.category) ?? 'Uploaded',
    bpm,
    beats,
    beatsPerBar,
    seconds,
    file: path,
    url,
    storagePath: path,
    rights: str(row.rights) ?? 'review-required',
    ...(row.tempo_verified === false ? { tempoVerified: false } : {}),
  };
}

// --- the list, shared by every consumer -------------------------------------

let loops: UploadedLoop[] = [];
let loadedFor: string | null = null;
let pending: Promise<UploadedLoop[]> | null = null;
const listeners = new Set<() => void>();

function publish(next: UploadedLoop[]) {
  loops = next;
  for (const listener of listeners) listener();
}

/** Every uploaded loop the signed-in member may play. [] signed out. */
export function uploadedLoops(): Promise<UploadedLoop[]> {
  const auth = getAuthState();
  const who = auth.status === 'active' ? auth.userId : null;
  if (!who && !testClient) {
    if (loops.length) publish([]);
    loadedFor = null;
    return Promise.resolve([]);
  }
  if (loadedFor !== who) pending = null;
  pending ??= (async () => {
    try {
      const c = await client();
      if (!c) return [];
      const { data, error } = await c
        .from(TABLE)
        .select(
          'id, label, category, bpm, beats, beats_per_bar, seconds, tempo_verified, storage_path, rights, created_at',
        );
      if (error || !data) throw new Error(error?.message ?? 'no data');
      const paths = data.map((row) => str(row.storage_path)).filter((p): p is string => !!p);
      const urls = new Map<string, string>();
      if (paths.length) {
        const signed = await c.storage.from(LOOP_BUCKET).createSignedUrls(paths, SIGNED_SECONDS);
        for (const entry of signed.data ?? []) {
          if (entry.path && entry.signedUrl) urls.set(entry.path, entry.signedUrl);
        }
      }
      const list = data
        .map((row) => loopOfRow(row, urls.get(str(row.storage_path) ?? '')))
        .filter((loop): loop is UploadedLoop => loop !== null)
        .sort((a, b) => a.label.localeCompare(b.label));
      loadedFor = who;
      publish(list);
      return list;
    } catch {
      pending = null;
      return loops;
    }
  })();
  return pending;
}

/** Fetch the list again (an admin added or removed a loop). */
export function refreshUploadedLoops(): Promise<UploadedLoop[]> {
  pending = null;
  return uploadedLoops();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The uploaded loops, live, refetched when who is signed in changes. */
export function useUploadedLoops(): UploadedLoop[] {
  const list = useSyncExternalStore(subscribe, () => loops);
  useEffect(() => {
    void uploadedLoops();
    return subscribeAuth(() => void uploadedLoops());
  }, []);
  return list;
}

/** The kit picker's list: the bundled loops, then the uploaded ones. */
export function useDrumLoops(): DrumLoop[] {
  const [bundled, setBundled] = useState<DrumLoop[]>([]);
  useEffect(() => {
    let live = true;
    void loadDrumManifest().then((all) => live && setBundled(all));
    return () => {
      live = false;
    };
  }, []);
  const uploaded = useUploadedLoops();
  return useMemo(
    () => (uploaded.length ? [...bundled, ...uploaded] : bundled),
    [bundled, uploaded],
  );
}

// --- the admin's half ----------------------------------------------------------

export interface LoopUpload {
  file: Blob;
  fileName: string;
  label: string;
  category: string;
  bpm: number;
  beats: number;
  beatsPerBar: number;
  seconds: number;
  tempoVerified: boolean;
  sha256: string;
  rights: 'owned' | 'licensed' | 'public-domain' | 'review-required';
  rightsNote: string;
}

const EXTENSIONS: Record<string, string> = {
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/aac': 'aac',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/wave': 'wav',
  'audio/ogg': 'ogg',
  'audio/flac': 'flac',
};

/** The file's audio type, from the browser or failing that its name. */
export function loopMime(file: Blob, fileName: string): string | null {
  if (file.type && EXTENSIONS[file.type]) return file.type;
  const ext = fileName.toLowerCase().split('.').pop();
  const byExt: Record<string, string> = {
    m4a: 'audio/mp4',
    aac: 'audio/aac',
    mp3: 'audio/mpeg',
    wav: 'audio/wav',
    ogg: 'audio/ogg',
    flac: 'audio/flac',
  };
  return (ext && byExt[ext]) || null;
}

/** Decode an audio file and return its length in seconds. */
export async function decodeDuration(bytes: ArrayBuffer): Promise<number> {
  const ctx = new OfflineAudioContext(1, 1, 44100);
  const buffer = await ctx.decodeAudioData(bytes.slice(0));
  return buffer.duration;
}

/** Upload the audio (named by its checksum) and describe it. Throws a plain reason. */
export async function uploadDrumLoop(upload: LoopUpload): Promise<void> {
  const c = await client();
  if (!c) throw new Error('Uploads need the account backend.');
  const mime = loopMime(upload.file, upload.fileName);
  if (!mime) throw new Error('That file is not an audio type the kit can play.');
  const path = `${upload.sha256}.${EXTENSIONS[mime]}`;
  const stored = await c.storage
    .from(LOOP_BUCKET)
    .upload(path, upload.file, { contentType: mime, upsert: false });
  // The same bytes uploaded twice are one file: a second row may reuse it.
  if (stored.error && !/exists|duplicate/i.test(stored.error.message)) {
    throw new Error(stored.error.message);
  }
  const { error } = await c
    .from(TABLE)
    .insert({
      label: upload.label,
      category: upload.category,
      bpm: upload.bpm,
      beats: upload.beats,
      beats_per_bar: upload.beatsPerBar,
      seconds: upload.seconds,
      tempo_verified: upload.tempoVerified,
      sha256: upload.sha256,
      mime,
      size_bytes: upload.file.size,
      storage_path: path,
      rights: upload.rights,
      rights_note: upload.rightsNote,
    })
    .select('id');
  if (error) {
    throw new Error(
      /unique|duplicate/i.test(error.message) ? 'That loop is already uploaded.' : error.message,
    );
  }
  await refreshUploadedLoops();
}

/** Remove an uploaded loop: its row, then its audio. */
export async function deleteDrumLoop(loop: { id: string; storagePath: string }): Promise<void> {
  const c = await client();
  if (!c) throw new Error('Uploads need the account backend.');
  const { error } = await c.from(TABLE).delete().eq('id', loop.id);
  if (error) throw new Error(error.message);
  await c.storage.from(LOOP_BUCKET).remove([loop.storagePath]);
  await refreshUploadedLoops();
}

// --- test seams ----------------------------------------------------------------

export function __setUploadedLoopsTestClient(c: LoopsClient | null): void {
  testClient = c;
}

export function __resetUploadedLoopsForTests(): void {
  loops = [];
  loadedFor = null;
  pending = null;
}
